import React, { useEffect, useState, useCallback } from 'react';
import {
  Table,
  Avatar,
  Select,
  Card,
  Row,
  Col,
  Input,
  Spin,
  message,
  Button,
  Tag,
  Tabs,
  Tooltip,
  Modal,
  Switch,
  Badge,
  Empty,
  Alert,
  Pagination,
} from 'antd';
import { useNavigate } from 'react-router-dom';
import { PlusOutlined, UserOutlined } from '@ant-design/icons';
import './patientList.css';
import drEasyApi from '../../../Api/Customapi';
import { getImg } from '../../../Api/getImg';
import ConsultantAssignmentModal from './ConsultantAssignmentModal';
import {
  FaBuilding,
  FaRedo,
  FaSearch,
  FaKeyboard,
  FaWifi,
  FaExclamationTriangle,
  FaFilter,
  FaUsers,
  FaToggleOn,
  FaInfoCircle,
  FaUser,
  FaEnvelope,
  FaHospital,
  FaUserMd,
  FaEdit
} from 'react-icons/fa';
import { BiUserPlus } from 'react-icons/bi';
import CreatePatientModal from './CreatePatientModal';
import SpecialistAssignmentModal from '../BookingsScreen/SpecialistAssignModal';
import { FaUserDoctor } from 'react-icons/fa6';
import PatientInfoModal from '../BookingsScreen/PatientInfoModal';

const { Option } = Select;
const { Search } = Input;
const { TabPane } = Tabs;
const { confirm } = Modal;
const PAGE_SIZE = 12;

const getResponsiveMode = () => {
  if (window.innerWidth <= 768) return 'mobile';
  if (window.innerWidth <= 992) return 'tablet';
  return 'desktop';
};

// Custom hook for online status
const useOnlineStatus = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return isOnline;
};

// Debounce hook
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => clearTimeout(handler);
  }, [value, delay]);

  return debouncedValue;
};

// Enhanced PatientCard Component
const PatientCard = React.memo(({
  patient,
  index,
  onNavigate,
  onStatusChange,
  onResetSkipCount,
  onAssignConsultant,
  changingStatus,
  resetingSkipCount,
  responsiveMode,
  onAssignSpecialist,
  setPatientInfoModalVisible

}) => {
  const isMobile = responsiveMode === 'mobile';
  const isTablet = responsiveMode === 'tablet';

  const handleCardClick = useCallback(
    (e) => {
      if (e.target.closest(".patient-card-actions")) return;
      onNavigate(patient);
    },
    [patient, onNavigate],
  );

  const handleStatusChange = useCallback((checked, e) => {
    e.stopPropagation();
    onStatusChange(patient.accountId, patient.isDeactivated, patient.fullName);
  }, [patient, onStatusChange]);

  const handleResetClick = useCallback((e) => {
    e.stopPropagation();
    onResetSkipCount(patient.id, patient.fullName, patient.skippedCount);
  }, [patient, onResetSkipCount]);

  const handleAssignClick = useCallback((e) => {
    e.stopPropagation();
    onAssignConsultant(patient);
  }, [patient, onAssignConsultant]);
  const handleAssignSpecialistClick = useCallback((e) => {
    e.stopPropagation();
    onAssignSpecialist(patient);
  }, [patient, onAssignSpecialist]);
  const handleEdit = useCallback((e) => {
    e.stopPropagation();
    setPatientInfoModalVisible(true);
  }, [patient]);
  const role=localStorage.getItem('role')
  return (
    <Card
      onClick={handleCardClick}
      hoverable
      className={`
        group relative border-orange-300 bg-white rounded-xl sm:rounded-2xl shadow-md hover:shadow-xl border-secondary/0
        transition-all duration-300 transform hover:-translate-y-1 
        border border-gray-200 hover:border-blue-300 overflow-hidden cursor-pointer
        ${isMobile ? 'mb-4' : ''}
      `}
      style={{
        animationDelay: `${index * 100}ms`,
        animation: 'fadeInUp 0.6s ease-out forwards'
      }}
    >
      {/* Assign to Consultant Button */}
      <div className={`absolute top-2 right-2 sm:top-4 sm:right-4 z-10`}>
        <div className="patient-card-actions flex gap-2" onClick={(e) => e.stopPropagation()}>

          <Tooltip title="Assign to Consultant">
            <Button
              type="primary"
              icon={<FaUserDoctor className='md:text-lg' />}
              size={isMobile ? "small" : "middle"}
              onClick={handleAssignClick}
              className="rounded-lg bg-primary hover:bg-primary_hover border-none"
            />
          </Tooltip>

          {/* New specialist button */}
          <Tooltip title="Assign to Specialist">
            <Button
              type="primary"
              icon={<FaUserMd className='md:text-lg' />}
              size={isMobile ? "small" : "middle"}
              onClick={handleAssignSpecialistClick}
              className="rounded-lg bg-teal-600 hover:bg-teal-700 border-none"
            />
          </Tooltip>

        </div>

      </div>

      {/* Card Content */}
      <div className="p-4 sm:p-6">
        {/* Patient Header */}
        <div className={`flex items-center mb-3 sm:mb-4 ${isMobile ? 'flex-wrap' : ''}`}>
          <div className="relative flex-shrink-0">
            <Avatar
              src={getImg(patient.imageId)}
              size={isMobile ? 48 : isTablet ? 56 : 64}
              style={{ backgroundColor: '#298E9F' }}
              className="shadow-lg ring-2 ring-white"
            >
              {!getImg(patient.imageId) && patient.fullName?.charAt(0)?.toUpperCase()}
            </Avatar>
            <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white"></div>
          </div>

          <div className="flex items-center gap-2 ml-3 sm:ml-4 min-w-0 flex-1">
            <h3 className={`font-bold text-gray-800 group-hover:text-blue-600 transition-colors duration-300 ${isMobile ? 'text-base' : 'text-lg'
              } truncate`}>
              {patient.fullName}
            </h3>
            {role==4&&
            <Tooltip title="Edit patient">

              <Button
                title='Edit'
                size='small'
                icon={<FaEdit  />}
                onClick={handleEdit}

              />
            </Tooltip>}
          </div>
        </div>

        {/* Patient Details */}
        <div className="space-y-3">
          {/* Email */}
          <div className={`p-3 sm:p-4 rounded-lg bg-green-50 border flex flex-col`}>
            <div className="flex items-center">
              <FaEnvelope className={`text-blue-500 mr-3 flex-shrink-0 ${isMobile ? 'text-sm' : ''}`} />
              <div className="min-w-0 flex-1">
                <div className={`text-xs text-gray-600 font-medium`}>Email</div>
                <Tooltip title={patient.email}>
                  <div className={`text-gray-800 truncate ${isMobile ? 'text-sm' : ''}`}>
                    {patient.email}
                  </div>
                </Tooltip>
              </div>
            </div>
            <div className="flex items-center mt-3">
              <FaHospital className={`text-green-500 mr-3 flex-shrink-0 ${isMobile ? 'text-sm' : ''}`} />
              <div className={`text-gray-800 ${isMobile ? 'text-sm' : ''}`}>
                {patient.clinics?.[0]?.name || 'N/A'} Clinic
              </div>
            </div>
          </div>

          {/* Skip Count */}
          {patient.accountId && (
            <div className={`p-3 sm:px-4 rounded-lg ${patient.skippedCount > 0 ? 'bg-orange-50 border-orange-200' : 'bg-gray-50 border-gray-200'} border`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <FaRedo className={`${patient.skippedCount > 0 ? 'text-orange-500' : 'text-gray-500'} mr-3 flex-shrink-0 ${isMobile ? 'text-sm' : ''}`} />
                  <div>
                    <div className={`text-xs text-gray-600 mb-1 font-medium`}>Skip Count</div>
                    <Tag
                      color={patient.skippedCount > 0 ? 'orange' : 'green'}
                      className="m-0"
                    >
                      {patient.skippedCount || 0}
                    </Tag>
                  </div>
                </div>
                {patient.skippedCount > 0 && (
                  <div className="patient-card-actions">
                    <Tooltip title="Reset skip count">
                      <Button
                        type="primary"
                        size="small"
                        icon={<FaRedo />}
                        loading={resetingSkipCount === patient.id}
                        onClick={handleResetClick}
                        className="bg-blue-500 border-none rounded-full shadow-lg hover:bg-blue-600 transform hover:scale-110 transition-all duration-300"
                      />
                    </Tooltip>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        {patient.accountId && (
          <div className={`flex items-center justify-between pt-4 border-t border-gray-100`}>
            <div className="patient-card-actions" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center space-x-3">
                <div className="flex items-center space-x-2">
                  <span className={`text-gray-600 font-medium ${isMobile ? 'text-xs' : 'text-sm'}`}>
                    Status:
                  </span>
                  <Switch
                    checked={!patient.isDeactivated}
                    loading={changingStatus === patient.id}
                    onChange={handleStatusChange}
                    checkedChildren="Active"
                    unCheckedChildren="Inactive"
                    size={isMobile ? 'small' : 'default'}
                    style={{
                      backgroundColor: !patient.isDeactivated ? '#45ac12ff' : undefined,
                    }}
                  />
                </div>
              </div>
            </div>

            <div className={`text-xs text-gray-400 ${isMobile ? '' : 'ps-2'}`}>
              Click to view details
            </div>
          </div>
        )}
      </div>
    </Card>
  );
});

const PatientsList = () => {
  const navigate = useNavigate();
  const isOnline = useOnlineStatus();

  const [patients, setPatients] = useState([]);
  const [clinicOptions, setClinicOptions] = useState([]);
  const [selectedClinicId, setSelectedClinicId] = useState(null);
  const [searchName, setSearchName] = useState('');
  const [activeTab, setActiveTab] = useState('pending');
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [resetingSkipCount, setResetingSkipCount] = useState(null);
  const [changingStatus, setChangingStatus] = useState(null);
  const [responsiveMode, setResponsiveMode] = useState(getResponsiveMode());
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const [networkError, setNetworkError] = useState(false);
  const [createPatientModalVisible, setCreatePatientModalVisible] = useState(false);
  // Consultant Assignment Modal State
  const [consultantModalVisible, setConsultantModalVisible] = useState(false);
  const [selectedPatientForAssignment, setSelectedPatientForAssignment] = useState(null);
  const [specialistModalVisible, setSpecialistModalVisible] = useState(false);
  const [selectedPatientForSpecialist, setSelectedPatientForSpecialist] = useState(null);
  // Debounce search input
  const debouncedSearchName = useDebounce(searchName, 300);
  const [patientInfoModalVisible, setPatientInfoModalVisible] = useState(false);
  const [selectedPatientForInfo, setSelectedPatientForInfo] = useState(null);

  // Applied filters for display
  const appliedFilters = [];
  if (debouncedSearchName) appliedFilters.push({ type: 'search', value: debouncedSearchName });
  if (selectedClinicId) {
    const clinic = clinicOptions.find(c => c.id === selectedClinicId);
    appliedFilters.push({ type: 'clinic', value: clinic?.name || 'Unknown Clinic' });
  }

  // Get responsive grid columns
  const getGridCols = () => {
    if (responsiveMode === 'mobile') return 1;
    if (responsiveMode === 'tablet') return 2;
    return 3;
  };

  // Network status monitoring
  useEffect(() => {
    const handleOnline = () => {
      setNetworkError(false);
    };
    const handleOffline = () => {
      setNetworkError(true);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Fetch patients with retry mechanism
  const fetchPatients = useCallback(async (showLoading = true) => {
    if (showLoading) setLoading(true);
    setError(null);

  const isPendingTab = activeTab === 'pending';
const deactivatedParam = activeTab === 'inactive';

const params = {
  NameFilter: debouncedSearchName || undefined,
  ...(isPendingTab
    ? { havePending: true }
    : { deactivated: deactivatedParam }),
  Page: page,
  PageSize: PAGE_SIZE,
};

    try {
      const res = await drEasyApi.get('/api/Centers/getPatients', {
        params,
      });

      const normalizedPatients = (res.data.patients || []).map(patient => ({
        ...patient,
        isDeactivated: patient.isDeactivated ?? patient.isDeactiveted ?? false
      }));

      setPatients(normalizedPatients);
      setTotalCount(res.data.totalCount || 0);
      setRetryCount(0);
      setNetworkError(false);
    } catch (error) {
      if (error.response?.status === 403) {
        window.location.href = "/sub";
      }
      console.error('Fetch patients error:', error);
      setError(error);

      if (!navigator.onLine || error.code === 'NETWORK_ERROR' || error.message?.includes('network') || error.message?.includes('fetch')) {
        setNetworkError(true);
      }
    } finally {
      if (showLoading) setLoading(false);
    }
  }, [debouncedSearchName, selectedClinicId, activeTab, page, isOnline]);

  useEffect(() => {
    fetchPatients();
  }, [fetchPatients]);

  const handleSearchChange = (e) => {
    setSearchName(e.target.value);
    setPage(1);
  };

  const handleFilterChange = (value) => {
    setSelectedClinicId(value);
    setPage(1);
  };

  const handleTabChange = (key) => {
    setActiveTab(key);
    setPage(1);
  };

  const clearAllFilters = () => {
    setSearchName('');
    setSelectedClinicId(null);
    setPage(1);
  };

  useEffect(() => {
    const handleResize = () => setResponsiveMode(getResponsiveMode());
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handlePatientNavigate = useCallback(
    (patient) => {
      navigate(`/patients/${patient.id}`, { state: { patient } });
    },
    [navigate],
  );

  // Enhanced status change with confirmation
  const handleStatusChange = async (userId, currentStatus, patientName) => {
    const actionText = currentStatus ? 'activate' : 'deactivate';

    confirm({
      title: 'Confirm Status Change',
      content: (
        <div>
          <p>Are you sure you want to <strong>{actionText}</strong> this patient?</p>
          <p className="text-gray-600 mt-2">Patient: <strong>{patientName}</strong></p>
        </div>
      ),
      okText: `Yes, ${actionText}`,
      okType: currentStatus ? 'primary' : 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          setChangingStatus(userId);
          await drEasyApi.put('/api/Doctor/cahngeUserActviation', {
            userId: userId
          });

          const newStatusText = currentStatus ? 'Active' : 'Inactive';
          message.success(`Patient status changed to ${newStatusText}`);
          await fetchPatients(false);
        } catch (error) {
          message.error('Failed to change patient status');
          console.error('Status change error:', error);
        } finally {
          setChangingStatus(null);
        }
      },
    });
  };
  const handleAssignToSpecialist = useCallback((patient) => {
    const bookingData = {
      userId: patient.accountId,
      patientId: patient.id,
      patientName: patient.fullName,
      doctorId: null,
    };
    setSelectedPatientForSpecialist(bookingData);
    setSpecialistModalVisible(true);
  }, []);

  const handleSpecialistAssigned = useCallback((specialist) => {
    message.success(`Successfully assigned ${selectedPatientForSpecialist?.patientName} to ${specialist.name}`);
    setSpecialistModalVisible(false);
    setSelectedPatientForSpecialist(null);
    fetchPatients(false);
  }, [selectedPatientForSpecialist, fetchPatients]);

  const handleSpecialistModalCancel = useCallback(() => {
    setSpecialistModalVisible(false);
    setSelectedPatientForSpecialist(null);
  }, []);
  // Enhanced skip count reset with confirmation
  const handleResetSkipCount = async (userId, patientName, skipCount) => {
    confirm({
      title: 'Reset Skip Count',
      content: (
        <div>
          <p>Are you sure you want to reset the skip count for this patient?</p>
          <p className="text-gray-600 mt-2">Patient: <strong>{patientName}</strong></p>
          <p className="text-gray-600">Current skip count: <strong>{skipCount}</strong></p>
        </div>
      ),
      okText: 'Yes, Reset',
      okType: 'primary',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          setResetingSkipCount(userId);
          await drEasyApi.put('/api/Doctor/resetSkipCount', {
            userId: userId
          });

          message.success(`Skip count reset for ${patientName}`);
          await fetchPatients(false);
        } catch (error) {
          message.error('Failed to reset skip count');
          console.error('Reset skip count error:', error);
        } finally {
          setResetingSkipCount(null);
        }
      },
    });
  };

  // Handle Assign to Consultant
  const handleAssignToConsultant = useCallback((patient) => {
    // Create a session-like object for the ConsultantAssignmentModal
    const sessionData = {
      userId: patient.accountId,
      patientId: patient.id,
      patientName: patient.fullName,
      profileID: patient.id, // Add this if you have a profile ID
    };
    setSelectedPatientForAssignment(sessionData);
    setConsultantModalVisible(true);
  }, []);

  const handleConsultantAssigned = useCallback((consultant) => {
    message.success(`Successfully assigned ${selectedPatientForAssignment?.patientName} to Dr. ${consultant.name}`);
    setConsultantModalVisible(false);
    setSelectedPatientForAssignment(null);
    // Optionally refresh the patients list
    fetchPatients(false);
  }, [selectedPatientForAssignment, fetchPatients]);

  const handleConsultantModalCancel = useCallback(() => {
    setConsultantModalVisible(false);
    setSelectedPatientForAssignment(null);
  }, []);
  const handlePatientInfoModalClose = useCallback(() => {
    setPatientInfoModalVisible(false);
    setSelectedPatientForInfo(null);  // ← clear it on close
  }, []);
  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6">
      {/* Add custom CSS for animations */}
      <style jsx>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>

      {/* Network Error Alert */}
      {networkError && (
        <Alert
          message="Connection Issue"
          description={
            <div className="flex items-center justify-between">
              <span>
                <FaWifi className="inline mr-2" />
                You're currently offline or experiencing connectivity issues. Some features may not work properly.
              </span>
              <Button size="small" onClick={() => fetchPatients()}>
                Retry
              </Button>
            </div>
          }
          type="warning"
          showIcon
          closable
          className="mb-4"
        />
      )}

      {/* Header */}
      <div className="mb-4 sm:mb-6">
        {/* Search and Filter Controls */}
        <div className="space-y-4 sm:space-y-0 sm:flex sm:flex-col lg:flex-row lg:justify-between lg:items-start gap-4">
          {/* Search Section */}
          <div className="w-full">
            <Search
              placeholder="Search by patient name..."
              allowClear
              value={searchName}
              onChange={handleSearchChange}
              className="custom-search w-full"
              prefix={<FaSearch className="text-gray-400" />}
              data-tip="search"
            />
          </div>
          <Button
            type=""
            icon={<PlusOutlined />}
            className="bg-primary text-white flex items-center gap-2"
            onClick={setCreatePatientModalVisible}
          // onClick={() => document.querySelector('.custom-search input')?.focus()}
          >
            <span className="hidden sm:inline">Add Patient</span>
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs activeKey={activeTab} onChange={handleTabChange} className="mb-4" data-tip="patient-tabs">
              <TabPane
  tab={
    <div className="flex items-center gap-2">
      <FaUsers />
      Active Patients
      {activeTab === 'pending' && patients.length > 0 && (
        <Badge count={totalCount} size="small" style={{ backgroundColor: '#faad14' }} />
      )}
    </div>
  }
  key="pending"
/>
        <TabPane
          tab={
            <div className="flex items-center gap-2">
              <FaUsers />
              All Patients
              {activeTab === 'active' && patients.length > 0 && (
                <Badge count={totalCount} size="small" style={{ backgroundColor: '#52c41a' }} />
              )}
            </div>
          }
          key="active"
        />
  
        <TabPane
          tab={
            <div className="flex items-center gap-2">
              <FaUsers />
              Blocked Patients
              {activeTab === 'inactive' && patients.length > 0 && (
                <Badge count={totalCount} size="small" style={{ backgroundColor: '#ff4d4f' }} />
              )}
            </div>
          }
          key="inactive"
        />
      </Tabs>

      {/* Content */}
      <Spin spinning={loading}>
        {!loading && patients.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              appliedFilters.length > 0
                ? "No patients match your search criteria"
                : "No patients found"
            }
            className="my-8"
          >
            {appliedFilters.length > 0 && (
              <Button type="primary" onClick={clearAllFilters}>
                Clear Filters
              </Button>
            )}
          </Empty>
        ) : (
          <>
            {/* Card Grid Layout */}
            <div className={`grid grid-cols-1 ${responsiveMode === 'mobile' ? 'gap-4' :
              responsiveMode === 'tablet' ? 'sm:grid-cols-2 gap-4 sm:gap-6' :
                'sm:grid-cols-2 lg:grid-cols-3 gap-6'
              } mb-6 sm:mb-8`}>
              {patients.map((patient, index) => (
                <PatientCard
                  key={patient.id}
                  patient={patient}
                  index={index}
                  onNavigate={handlePatientNavigate}
                  onStatusChange={handleStatusChange}
                  onResetSkipCount={handleResetSkipCount}
                  onAssignConsultant={handleAssignToConsultant}
                  changingStatus={changingStatus}
                  resetingSkipCount={resetingSkipCount}
                  responsiveMode={responsiveMode}
                  onAssignSpecialist={handleAssignToSpecialist}
                  setPatientInfoModalVisible={(val) => {
                    setSelectedPatientForInfo(patient);
                    setPatientInfoModalVisible(val);
                  }} patientInfoModalVisible={patientInfoModalVisible}

                />
              ))}
            </div>

            {/* Enhanced Responsive Pagination */}
            {totalCount > PAGE_SIZE && (
              <div className="flex justify-center">
                <div className={`bg-white rounded-xl sm:rounded-2xl shadow-lg ${responsiveMode === 'mobile' ? 'p-2' : 'p-4'}`}>
                  <Pagination
                    current={page}
                    pageSize={PAGE_SIZE}
                    total={totalCount}
                    onChange={(p) => setPage(p)}
                    showTotal={(total) => (
                      <span className={`text-gray-600 font-medium ${responsiveMode === 'mobile' ? 'text-xs' : ''}`}>
                        {responsiveMode === 'mobile' ? `${total} total` : `Total ${total} patients`}
                      </span>
                    )}
                    size={responsiveMode === 'mobile' ? 'small' : 'default'}
                    showSizeChanger={responsiveMode !== 'mobile'}
                    showQuickJumper={responsiveMode !== 'mobile'}
                    className="modern-pagination"
                  />
                </div>
              </div>
            )}
          </>
        )}
      </Spin>

      {/* Consultant Assignment Modal */}
      <ConsultantAssignmentModal
        visible={consultantModalVisible}
        onCancel={handleConsultantModalCancel}
        onAssign={handleConsultantAssigned}
        session={selectedPatientForAssignment}
        isMobile={responsiveMode === 'mobile'}
      />
      <SpecialistAssignmentModal
        visible={specialistModalVisible}
        onCancel={handleSpecialistModalCancel}
        onAssign={handleSpecialistAssigned}
        booking={selectedPatientForSpecialist}
        isMobile={responsiveMode === 'mobile'}
        handleCompleteClick={() => { }}   // no-op — no booking to complete from patient list
      />
      <CreatePatientModal visible={createPatientModalVisible} onClose={() => setCreatePatientModalVisible(false)} />
      <PatientInfoModal
        visible={patientInfoModalVisible}
        onCancel={handlePatientInfoModalClose}
        patientId={selectedPatientForInfo?.accountId}
        fetchPatients={fetchPatients}

      />
    </div>
  );
};

PatientCard.displayName = 'PatientCard';

export default PatientsList;